import Hls from 'hls.js';
import type { HlsConfig, Loader, LoaderCallbacks, LoaderConfiguration, LoaderContext } from 'hls.js';

export interface RecordingLoaderOptions {
	/** The playlist's key-request URI, used only to recognise which request to intercept. */
	keyUri?: string;
	/**
	 * Raw 16-byte AES-128 key (the locally derived recording key) injected to
	 * satisfy the key request. We never fetch the key the playlist points to.
	 */
	keyBytes?: Uint8Array | null;
	/**
	 * GCS signed-URL query string (`URLPrefix=...&Expires=...&KeyName=...&Signature=...`)
	 * appended to storage requests. Recordings are served from cloud storage and
	 * authenticated via signed URL rather than a bearer token.
	 */
	signedQuery?: string;
	/** Only requests on this origin (the storage bucket) get the signed query appended. */
	signedOrigin?: string;
}

type LoaderConstructor = new (config: HlsConfig) => Loader<LoaderContext>;

/**
 * Dev-only trace of decryption-key request state: whether a key was injected or
 * missing, its byte length, and — since this only runs in dev builds — the raw
 * key hex. Never emits anything in production.
 */
const logKeyState = (state: 'injected' | 'missing', keyBytes?: Uint8Array | null): void => {
	if (!import.meta.env.DEV) return;
	const keyByteLength = keyBytes?.byteLength ?? 0;
	console.log('[recording-decrypt] key request', {
		state,
		keyByteLength,
		keyHex: keyByteLength
			? Array.from(keyBytes as Uint8Array)
					.map((b) => b.toString(16).padStart(2, '0'))
					.join('')
			: null,
	});
};

/**
 * A request is a decryption-key request when its URL matches the playlist's
 * key-request `keyUri`. We do not fetch or use the key the playlist points to;
 * the URI only tells us which request to intercept and answer with the locally
 * derived recording key.
 */
const isKeyRequest = (url: string, keyUri?: string): boolean => {
	if (!url || !keyUri) return false;
	return url === keyUri;
};

/**
 * Append the GCS signed-URL query string to a storage request, unless it is
 * cross-origin or already signed.
 *
 * @remarks
 * hls.js resolves segment URLs against the playlist's path, dropping its query
 * string, so the signed query must be re-appended to every storage request
 * (manifest + segments) rather than only the top-level playlist URL.
 *
 * @param url - The request URL.
 * @param signedQuery - The signed-URL query string to append.
 * @param signedOrigin - Only URLs on this origin are signed.
 * @returns The (possibly) signed URL.
 */
const signStorageUrl = (url: string, signedQuery?: string, signedOrigin?: string): string => {
	if (!signedQuery) return url;
	try {
		const parsed = new URL(url);
		if (signedOrigin && parsed.origin !== signedOrigin) return url;
		if (parsed.searchParams.has('Signature')) return url;
		const separator = url.includes('?') ? '&' : '?';
		return `${url}${separator}${signedQuery}`;
	} catch {
		return url;
	}
};

/**
 * Build an hls.js loader that intercepts the AES-128 key request (matched by the
 * playlist's `keyUri`) and answers it with the locally derived recording key
 * bytes, while delegating every other request (manifest, segments) to the
 * default loader. The key the playlist references is never fetched or used.
 */
export const createRecordingLoader = (options: RecordingLoaderOptions): LoaderConstructor => {
	const DefaultLoader = Hls.DefaultConfig.loader as unknown as LoaderConstructor;

	return class RecordingLoader extends DefaultLoader {
		load(
			context: LoaderContext,
			config: LoaderConfiguration,
			callbacks: LoaderCallbacks<LoaderContext>,
		): void {
			if (!isKeyRequest(context.url, options.keyUri)) {
				context.url = signStorageUrl(context.url, options.signedQuery, options.signedOrigin);
				super.load(context, config, callbacks);
				return;
			}

			const stats = this.stats;
			const { keyBytes } = options;

			if (!keyBytes || keyBytes.byteLength === 0) {
				logKeyState('missing', keyBytes);
				/** A key-load error lets the player prompt for a key. */
				callbacks.onError({ code: 0, text: 'Missing decryption key' }, context, null, stats);
				return;
			}

			logKeyState('injected', keyBytes);

			const now = self.performance.now();
			stats.loading.start = now;
			stats.loading.first = now;
			stats.loading.end = now;
			stats.loaded = keyBytes.byteLength;
			stats.total = keyBytes.byteLength;

			/** Copy so hls.js owns the buffer (avoids detaching ours). */
			const data = keyBytes.slice().buffer;
			callbacks.onSuccess({ url: context.url, data }, stats, context, null);
		}
	};
};
